import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  Search,
  Plus,
  Building2,
  Handshake,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { AddClientModal } from '@/components/modals/AddClientModal';
import {
  GET_ORGANIZATIONS,
  type GetOrganizationsData,
  type GetOrganizationsVars,
  type Organization,
} from '@/graphql/organizations';

// Pagination constants
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Custom hook for debounced value
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Client type (mapped from GraphQL Organization)
type ClientType = 'cfi' | 'psp';
type ClientStatus = 'prospect' | 'active' | 'inactive';

// Status badge configuration
const STATUS_BADGES: Record<ClientStatus, { label: string; className: string }> = {
  prospect: { label: 'Prospect', className: 'badge-warning' },
  active: { label: 'Active', className: 'badge-secondary' },
  inactive: { label: 'Inactive', className: 'badge-neutral' },
};

// Type filter options for PSP vs CFI
type TypeFilter = 'all' | 'cfi' | 'psp';

const TYPE_ICONS: Record<ClientType, typeof Building2> = {
  cfi: Building2,
  psp: Handshake,
};


/**
 * Map GraphQL organization to display format
 */
function mapOrganizationToClient(org: Organization) {
  return {
    id: org.id,
    name: org.name,
    type: org.type.toLowerCase() as ClientType,
    status: org.status.toLowerCase() as ClientStatus,
    contactName: org.primaryContact
      ? `${org.primaryContact.firstName} ${org.primaryContact.lastName}`
      : '—',
    contactEmail: org.primaryContact?.email || '—',
    createdAt: new Date(org.createdAt),
  };
}

export function ClientList() {
  const { canWrite } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search query for server-side search (300ms delay)
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Track if search was focused before state changes
  const wasSearchFocused = useRef(false);

  // Reset to page 1 when filters change, but preserve focus
  useEffect(() => {
    // Check if search was focused before the state change
    wasSearchFocused.current = document.activeElement === searchInputRef.current;
    
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, typeFilter, pageSize]);

  // Restore focus after state changes
  useEffect(() => {
    if (wasSearchFocused.current && searchInputRef.current) {
      searchInputRef.current.focus();
      wasSearchFocused.current = false;
    }
  });

  // Build query variables based on filters and pagination
  const getQueryVariables = useCallback((): GetOrganizationsVars => {
    const vars: GetOrganizationsVars = {
      limit: pageSize,
      offset: (currentPage - 1) * pageSize,
    };

    // Type filter from dropdown
    if (typeFilter === 'cfi') {
      vars.type = 'CFI';
    } else if (typeFilter === 'psp') {
      vars.type = 'PSP';
    }

    // Status filter from dropdown (only Active or Inactive)
    if (statusFilter !== 'all') {
      vars.status = statusFilter.toUpperCase() as 'ACTIVE' | 'INACTIVE';
    }

    // Server-side search
    if (debouncedSearch.trim()) {
      vars.search = debouncedSearch.trim();
    }

    return vars;
  }, [typeFilter, statusFilter, debouncedSearch, pageSize, currentPage]);

  // Fetch organizations from GraphQL API
  const { data, loading, error, refetch } = useQuery<GetOrganizationsData, GetOrganizationsVars>(
    GET_ORGANIZATIONS,
    {
      variables: getQueryVariables(),
      fetchPolicy: 'cache-and-network',
    }
  );

  // Extract data from response
  const organizations = data?.organizations?.items || [];
  const pageInfo = data?.organizations?.pageInfo;
  const totalCount = pageInfo?.totalCount || 0;
  const totalPages = pageInfo?.totalPages || 1;

  // Map organizations to display format, excluding prospects (prospects live under Sales)
  const clients = organizations
    .map(mapOrganizationToClient)
    .filter((client) => client.status !== 'prospect');

  const getPageTitle = () => {
    if (typeFilter === 'cfi') return 'CFI Clients';
    if (typeFilter === 'psp') return 'PSP Clients';
    return 'All Clients';
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-pond dark:text-pond-light mb-4" />
        <p className="text-chalkboard dark:text-rain-cloud">Loading clients...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <AlertCircle className="w-12 h-12 text-rust dark:text-salmon mb-4" />
        <h2 className="text-xl font-semibold text-slate dark:text-mist mb-2">
          Failed to load clients
        </h2>
        <p className="text-chalkboard dark:text-rain-cloud mb-4">
          {error.message}
        </p>
        <button onClick={() => refetch()} className="btn-primary">
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </button>
      </div>
    );
  }

  // Calculate display range
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate dark:text-mist">
            {getPageTitle()}
          </h1>
          <p className="text-chalkboard dark:text-rain-cloud mt-1">
            {totalCount === 0
              ? 'No clients found'
              : `Showing ${startItem}-${endItem} of ${totalCount} client${totalCount !== 1 ? 's' : ''}`}
            {debouncedSearch && ` matching "${debouncedSearch}"`}
            {loading && ' (refreshing...)'}
          </p>
        </div>
        {canWrite('clients') && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="btn-primary"
          >
            <Plus className="w-4 h-4" />
            Add Client
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem', alignItems: 'center' }}>
            {/* Search */}
            <div className="relative" style={{ flex: '1 1 auto', minWidth: 0 }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-french-gray pointer-events-none" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input"
                style={{ width: '100%', paddingLeft: '2.5rem' }}
              />
              {searchQuery && searchQuery !== debouncedSearch && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-french-gray animate-spin" />
              )}
            </div>
            {/* Type Filter (PSP vs CFI) */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
              className="select"
              style={{ flex: '0 0 auto', width: '10rem' }}
            >
              <option value="all">All Types</option>
              <option value="cfi">CFI</option>
              <option value="psp">PSP</option>
            </select>
            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'active' | 'inactive' | 'all')}
              className="select"
              style={{ flex: '0 0 auto', width: '10rem' }}
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {/* Page Size Selector */}
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="select"
              style={{ flex: '0 0 auto', width: '8rem' }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
            {/* Refresh button */}
            <button
              onClick={() => refetch()}
              disabled={loading}
              className="btn-secondary btn-icon"
              title="Refresh"
            >
              <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
            </button>
          </div>
        </div>
      </div>

      {/* Client Table */}
      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Type</th>
              <th>Status</th>
              <th>Primary Contact</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => {
              const TypeIcon = TYPE_ICONS[client.type];
              const statusBadge = STATUS_BADGES[client.status];

              return (
                <tr key={client.id}>
                  <td>
                    <Link
                      to={`/clients/${client.id}`}
                      className="font-medium text-pond dark:text-pond-light hover:underline"
                    >
                      {client.name}
                    </Link>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <TypeIcon className="w-4 h-4 text-chalkboard dark:text-rain-cloud" />
                      <span className="text-sm">{client.type.toUpperCase()}</span>
                    </div>
                  </td>
                  <td>
                    <span className={cn('badge', statusBadge.className)}>
                      {statusBadge.label}
                    </span>
                  </td>
                  <td>
                    <div>
                      <p className="text-sm text-slate dark:text-mist">{client.contactName}</p>
                      <p className="text-xs text-chalkboard dark:text-rain-cloud">
                        {client.contactEmail}
                      </p>
                    </div>
                  </td>
                  <td className="text-sm text-chalkboard dark:text-rain-cloud">
                    {formatDate(client.createdAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {clients.length === 0 && !loading && (
          <div className="p-8 text-center">
            <p className="text-chalkboard dark:text-rain-cloud">
              {searchQuery ? 'No clients match your search.' : 'No clients found.'}
            </p>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-french-gray/20 dark:border-rain-cloud/20">
            <div className="text-sm text-chalkboard dark:text-rain-cloud">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={!pageInfo?.hasPreviousPage || loading}
                className="btn-secondary btn-sm"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={!pageInfo?.hasNextPage || loading}
                className="btn-secondary btn-sm"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      <AddClientModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={(data) => {
          console.log('New client data:', data);
          // Refetch after modal closes (the modal will handle the mutation)
          refetch();
        }}
      />
    </div>
  );
}
